import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Store, Clock, Award, ChefHat, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import HeroCarousel from '../components/HeroCarousel';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// User-provided images - Mad Brothers Bakery actual photos
const userImages = {
  product1: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/ukilq0v9_WhatsApp%20Image%202026-02-06%20at%2018.51.06.jpeg',
  product2: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/yd9nyna5_WhatsApp%20Image%202026-02-06%20at%2018.50.23.jpeg',
  storefront: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/aer2b31i_WhatsApp%20Image%202026-02-06%20at%2018.50.01.jpeg',
  exterior: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/d1usdq8a_WhatsApp%20Image%202026-02-06%20at%2018.49.24.jpeg',
};

// Category images for horizontal panels
const categoryPanelImages = {
  celebration: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=1200&q=90',
  bespoke: 'https://images.unsplash.com/photo-1562440499-64c9a111f713?w=1200&q=90',
  cupcakes: 'https://images.unsplash.com/photo-1614707267537-b85aaf00c4b7?w=1200&q=90',
  seasonal: 'https://images.unsplash.com/photo-1481391319762-47dff72954d9?w=1200&q=90',
  catering: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=1200&q=90',
};

export default function HomePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setAllProducts(response.data);
        setProducts(response.data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchProducts();
    fetchCategories();
  }, []);

  const handleQuickAdd = async (product) => {
    try {
      await addToCart({ product_id: product.id, quantity: 1 });
      toast.success(`${product.name} added to cart!`, {
        action: {
          label: 'View Cart',
          onClick: () => navigate('/cart'),
        },
        duration: 5000,
        style: { cursor: 'pointer' },
        onClick: () => navigate('/cart'),
      });
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="home-page">
      <SEO
        title="Artisan Cakes in Dublin 7 - Custom Birthday, Communion & Bento Cakes"
        description="Mad Brothers Bakery in Dublin 7 (Ashtown). Handcrafted celebration cakes, communion & confirmation cakes, fresh cream cakes, bento cakes and cupcake boxes. Order online for collection."
        keywords="bakery Dublin, birthday cakes Dublin, custom cakes Dublin, communion cakes Dublin, fresh cream cakes Dublin, bento cakes Dublin, artisan bakery Ashtown, cake shop Dublin 7, celebration cakes Dublin"
      />
      {/* Hero Carousel */}
      <HeroCarousel />

      {/* Tagline Banner */}
      <section className="py-8 bg-white border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p
              className="font-['Playfair_Display'] italic font-bold text-3xl sm:text-4xl lg:text-5xl text-[#D4AF37] tracking-tight"
              style={{ letterSpacing: '-0.01em', WebkitTextStroke: '0.6px #D4AF37' }}
            >
              "Getting mad never felt so good."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Location Banner - Like Camerino */}
      <section className="py-12 bg-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#8D6E63] rounded-full flex items-center justify-center">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-2xl font-bold">Mad Brothers Bakery</h3>
                <p className="text-stone-300">17 Nephin Road, Ashtown, Dublin 7, D07 N8K7</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-stone-300">Fresh bakes daily from our Dublin 7 kitchen!</p>
              <p className="text-[#D4AF37] font-medium mt-1">
                Collection available • 48hr notice required
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shop by Category - Visual Grid */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 sm:mb-14"
          >
            <span className="font-handwritten text-2xl text-[#D4AF37]">Browse our</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight mt-1">
              Collections
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-3 max-w-xl mx-auto">
              Pick a collection to explore our handcrafted celebration cakes, cupcakes, artisan bread and more.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7" data-testid="home-categories-grid">
            {categories.map((cat, idx) => {
              const explicitCover = cat.image_url || cat.image || cat.cover_image;
              const firstProductImg = allProducts.find((p) => p.category === cat.id)?.image;
              const cover = explicitCover || firstProductImg || null;
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: idx * 0.05 }}
                >
                  <Link
                    to={`/shop?category=${encodeURIComponent(cat.id)}`}
                    className="group relative block aspect-[3/4] overflow-hidden rounded-2xl bg-gradient-to-br from-stone-100 to-stone-200 shadow-sm hover:shadow-2xl transition-all duration-500"
                    data-testid={`home-category-${cat.id}`}
                  >
                    {cover ? (
                      <img
                        src={cover}
                        alt={cat.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      // Elegant placeholder when no cover & no product image yet
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
                        <span className="font-['Playfair_Display'] text-7xl text-[#D4AF37]/40 italic">
                          {cat.name?.[0] || '?'}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                      <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight">
                        {cat.name}
                      </h3>
                      {cat.description && (
                        <p className="mt-1.5 text-white/80 text-sm line-clamp-2">
                          {cat.description}
                        </p>
                      )}
                      <span className="mt-3 inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-wider uppercase opacity-90 group-hover:gap-3 transition-all">
                        Shop now
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}

            {/* All Products tile */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: categories.length * 0.05 }}
            >
              <Link
                to="/shop?category=all"
                className="group relative block aspect-[3/4] overflow-hidden rounded-2xl bg-[#1a1a1a] hover:bg-black shadow-sm hover:shadow-2xl transition-all duration-500 flex items-center justify-center"
                data-testid="home-category-all"
              >
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#D4AF37_0%,transparent_50%)]" />
                <div className="relative z-10 text-center px-6">
                  <p className="font-['Cormorant_Garamond'] text-[#D4AF37] text-xs sm:text-sm font-bold tracking-[0.3em] uppercase mb-3">
                    Browse Everything
                  </p>
                  <h3 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white leading-tight">
                    All Products
                  </h3>
                  <span className="mt-5 inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-wider uppercase group-hover:gap-3 transition-all">
                    Explore
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="py-12 bg-white border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ChefHat, title: '30+ Years', desc: 'Family baking heritage' },
              { icon: Clock, title: '48hr Notice', desc: 'Fresh baked to order' },
              { icon: Store, title: 'Free Collection', desc: 'Dublin 7 bakery' },
              { icon: Award, title: 'Premium Quality', desc: 'Finest ingredients' },
            ].map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-4"
              >
                <div className="w-12 h-12 bg-[#8D6E63]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <feature.icon className="w-6 h-6 text-[#8D6E63]" />
                </div>
                <h4 className="font-semibold text-[#1a1a1a]">{feature.title}</h4>
                <p className="text-stone-500 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-[#1C1917] text-white relative grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="font-handwritten text-2xl text-[#D4AF37]">Our Heritage</span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold">
                30 Years of Family Baking Tradition
              </h2>
              <p className="text-stone-300 leading-relaxed">
                What started as a family passion three decades ago has blossomed into 
                Mad Brothers Bakery. In 2021, we brought our treasured recipes and 
                time-honored techniques to Dublin 7, where every bake is crafted with 
                the same love and dedication that has defined our family for generations.
              </p>
              <Link to="/about">
                <Button 
                  className="bg-[#8D6E63] hover:bg-[#8D6E63]/90 rounded-full px-8 mt-4"
                >
                  Learn Our Story
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={userImages.storefront}
                    alt="Mad Brothers Bakery interior"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={userImages.product1}
                    alt="Fresh baked goods"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={userImages.product2}
                    alt="Bakery display"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={userImages.exterior}
                    alt="Mad Brothers Bakery exterior"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#8D6E63]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white">
              Ready to Order Your Perfect Cake?
            </h2>
            <p className="text-white/80 mt-4 text-lg">
              Use code <span className="font-mono bg-white/20 px-2 py-1 rounded">MADBRO10</span> for 10% off your first order
            </p>
            <Link to="/shop">
              <Button 
                size="lg" 
                className="mt-8 bg-white text-[#1a1a1a] hover:bg-white/90 rounded-full px-10"
              >
                Start Your Order
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
